import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ConsiderationField,
    ConsiderationFieldChange,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import { ConsiderationHistoryPopover } from './ConsiderationHistoryPopover';

export interface EditableConsiderationCellHistoryProps {
    field: ConsiderationField;
    fieldChange?: ConsiderationFieldChange;
    engagementId: number;
}

const POPOVER_GAP_PX = 6;
const POPOVER_WIDTH_PX = 288;
const POPOVER_VIEWPORT_MARGIN_PX = 8;
const POPOVER_BOTTOM_CLEARANCE_PX = 380;

interface PopoverPosition {
    top: number;
    left: number;
}

export const EditableConsiderationCellHistory = ({
    field,
    fieldChange,
    engagementId,
}: EditableConsiderationCellHistoryProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<PopoverPosition | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLSpanElement>(null);
    const hasHistory = (fieldChange?.history.length ?? 0) > 0;

    const close = () => setIsOpen(false);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (popoverRef.current?.contains(target) || buttonRef.current?.contains(target)) {
                return;
            }
            close();
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };
        const handleScroll = (event: Event) => {
            const target = event.target;
            if (target instanceof Node && popoverRef.current?.contains(target)) {
                return;
            }
            close();
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isOpen]);

    if (!hasHistory || !fieldChange) {
        return null;
    }

    const openPopover = () => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (rect) {
            setPosition({
                top: Math.max(
                    POPOVER_VIEWPORT_MARGIN_PX,
                    Math.min(
                        rect.bottom + POPOVER_GAP_PX,
                        window.innerHeight - POPOVER_BOTTOM_CLEARANCE_PX
                    )
                ),
                left: Math.max(
                    POPOVER_VIEWPORT_MARGIN_PX,
                    Math.min(
                        rect.left,
                        window.innerWidth - (POPOVER_WIDTH_PX + POPOVER_VIEWPORT_MARGIN_PX)
                    )
                ),
            });
        }
        setIsOpen(true);
    };

    return (
        <span className="ff-editable-cell__history">
            <button
                ref={buttonRef}
                type="button"
                className={`ff-editable-cell__clock${
                    isOpen ? ' ff-editable-cell__clock--open' : ''
                }`}
                aria-label="View change history"
                title="View change history"
                onClick={() => (isOpen ? close() : openPopover())}>
                <FontAwesomeIcon icon={faClockRotateLeft} />
            </button>
            {isOpen &&
                position &&
                createPortal(
                    <ConsiderationHistoryPopover
                        ref={popoverRef}
                        field={field}
                        fieldChange={fieldChange}
                        engagementId={engagementId}
                        onClose={close}
                        style={{ position: 'fixed', top: position.top, left: position.left }}
                    />,
                    document.body
                )}
        </span>
    );
};
